<template>
    <v-app>
        <div>
            <md-progress-spinner v-if="isLoading"
                md-mode="indeterminate"></md-progress-spinner>
            <md-card v-else
                class="md-layout-item md-size-100 md-small-size-100">
                <md-card-content>
                    <div class="md-layout md-gutter">
                        <div class="md-layout-item md-size-75">
                            <md-field md-clearable
                                :class="{'md-invalid': $v.product.product_name.$dirty && $v.product.product_name.$invalid}">
                                <label>{{ $t('products.product-name') }}</label>
                                <md-input v-model="product.product_name"
                                    @input="$v.product.product_name.$touch()"></md-input>
                                <span class="md-error"
                                    v-show="$v.product.product_name.$dirty && $v.product.product_name.$invalid">{{ $t('validations.max-length',{num: $v.product.product_name.$params.maxLength.max}) }}. {{ $tc('validations.required-field', 1) }}</span>
                            </md-field>

                            <md-field md-clearable
                                :class="{'md-invalid': $v.product.scientific_name.$dirty && $v.product.scientific_name.$invalid}">
                                <label>{{ $t('products.scientific-name') }}</label>
                                <md-input v-model="product.scientific_name"
                                    @input="$v.product.scientific_name.$touch()"></md-input>
                                <span class="md-error"
                                    v-show="$v.product.scientific_name.$dirty && $v.product.scientific_name.$invalid">{{ $t('validations.max-length',{num: $v.product.scientific_name.$params.maxLength.max}) }}. {{ $tc('validations.required-field', 1) }}</span>
                            </md-field>

                            <template>
                                <v-container fluid
                                    grid-list-xl
                                    class="no-padding">
                                    <v-layout align-center
                                        wrap>
                                        <v-flex>
                                            <v-select v-model="product.category_id"
                                                :items="categories"
                                                item-value="category_id"
                                                item-text="category_name"
                                                attach
                                                :label="$tc('categories.category', 1)"
                                                required></v-select>
                                        </v-flex>
                                    </v-layout>
                                </v-container>
                            </template>

                            <template>
                                <v-container fluid
                                    grid-list-xl
                                    class="no-padding">
                                    <v-layout align-center
                                        wrap>
                                        <v-flex>
                                            <v-select v-model="product.calibers_items"
                                                :items="calibers"
                                                item-value="caliber_id"
                                                item-text="description"
                                                attach
                                                chips
                                                :label="$tc('calibers.caliber', 2)"
                                                multiple
                                                :deletable-chips="true"
                                                required></v-select>
                                        </v-flex>
                                    </v-layout>
                                </v-container>
                            </template>
                        </div>
                        <div class="md-layout-item md-size-25"
                            id="previewPicture">
                            <label class="labelTitle">{{ $t('products.image') }}</label>
                            <template>
                                <div class="base-image-input"
                                    :style="{ 'background-size':'contain','background-image': `url(${pictureData})` }"
                                    @click="chooseImage">
                                    <span v-if="!pictureData"
                                        class="placeholder">{{ $t('general.select-image') }}</span>
                                    <input class="file-input"
                                        ref="fileInput"
                                        type="file"
                                        @input="onSelectFile"
                                        accept="image/*">
                                </div>
                            </template>
                            <span class="error-msg"
                                v-show="$v.pictureData.$invalid">{{ $tc('validations.required-field',1) }}</span>
                        </div>
                        <div class="md-layout-item md-size-100">
                            <div class="action-bar">
                                <md-button class="md-raised"
                                    @click="close">
                                    {{ $t('actions.cancel') }}
                                </md-button>
                                <md-button class="md-raised md-primary"
                                    @click="saveProduct"
                                    :disabled="$v.$invalid">
                                    {{ $t('actions.confirm') }}
                                </md-button>
                            </div>
                        </div>
                    </div>
                </md-card-content>
            </md-card>
        </div>
    </v-app>
</template>

<script>
import _ from 'lodash'
import productsService from '@/components/products/services'
import categoriesService from '@/components/categories/services'
import calibersService from '@/components/calibers/services/'
import picturesService from '@/components/pictures/services/'
import { required, maxLength } from 'vuelidate/lib/validators'
import axios from 'axios'

const loadingKeys = {
    creatingItem: 'creating-item',
    fetchingItem: 'fetching-item'
}

export default {
    name: 'Productsmaintenance',
    data () {
        return {
            product: {
                product_id: null,
                product_name: '',
                scientific_name: '',
                category_id: null,
                picture_id: null,
                created_at: null,
                updated_at: null,
                deleted_at: null,
                picture_url: null,
                picture_name: '',
                calibers_items: []
            },
            calibers: [],
            categories: [],
            isViewMode: false,
            isAddMode: false,
            isEditMode: false,
            loadings: [],
            pictureData: ''
        }
    },
    validations: {
        product: {
            product_name: {
                required,
                maxLength: maxLength(100)
            },
            scientific_name: {
                required,
                maxLength: maxLength(100)
            },
            category_id: {
                required
            },
            calibers_items: {
                required
            }
        },
        pictureData: {
            required
        }
    },
    computed: {
        isLoading () {
            return this.isFetching || this.isCreatingProducts
        },

        isCreatingProducts () {
            return !!this.loadings.filter(
                loading => loading.key === loadingKeys.creatingItem
            ).length
        },

        isFetching () {
            return !!this.loadings.filter(
                loading => loading.key === loadingKeys.fetchingItem
            ).length
        }
    },
    created () {
        this.setMode(this.$route.name)

        this.fetchCalibers()
        this.fetchCategories()

        if (!this.isAddMode) {
            this.fetchProduct()
        }
    },
    methods: {
        setMode (mode) {
            switch (mode) {
                case 'view-product':
                    this.isViewMode = true
                    break
                case 'add-product':
                    this.isAddMode = true
                    break
                case 'edit-product':
                    this.isEditMode = true
                    break
            }
        },

        chooseImage () {
            this.$refs.fileInput.click()
        },

        onSelectFile () {
            const input = this.$refs.fileInput
            const files = input.files
            if (files && files[0]) {
                const reader = new FileReader()
                reader.onload = e => {
                    this.pictureData = e.target.result
                }
                reader.readAsDataURL(files[0])
                this.$emit('input', files[0])
            }
        },

        async saveProduct () {
            let self = this
            _.fill(this.loadings, loadingKeys.creatingItem)

            let productParams = {
                product_name: self.product.product_name,
                scientific_name: self.product.scientific_name,
                category_id: self.product.category_id,
                picture_id: self.product.picture_id,
                active: self.product.deleted_at ? 0 : 1,
                calibers_items: _.reduce(
                    self.product.calibers_items,
                    function (result, value, key) {
                        let caliber = {}
                        caliber.caliber_id = value
                        result.push(caliber)
                        return result
                    },
                    []
                )
            }

            let pictureParams = {
                name: self.product.product_name,
                data: _.split(self.pictureData, 'base64,')[1]
            }

            let response

            if (this.isAddMode) {
                response = await picturesService.create(pictureParams)
                if (response.data.picture_id) {
                    productParams.picture_id = response.data.picture_id
                } else {
                    return
                }
                response = await productsService.create(productParams)
            } else {
                pictureParams.picture_id = self.product.picture_id
                pictureParams.active = self.product.deleted_at ? 0 : 1
                response = await picturesService.update(pictureParams)

                productParams.product_id = self.product.product_id
                response = await productsService.update(productParams)
            }

            _.pull(this.loadings, loadingKeys.creatingItem)
            this.$router.go(-1)
        },

        async fetchProductCalibers () {
            let self = this

            let params = {
                filter: {
                    product_id: self.$route.params.id
                }
            }
            _.fill(this.loadings, loadingKeys.fetchingItem)

            let response = await productsService.getProductCalibers(params)
            let checkedIDs = _.cloneDeep(response.data)

            _.pull(this.loadings, loadingKeys.fetchingItem)

            return _.map(checkedIDs, 'caliber_id')
        },

        async fetchCalibers () {
            _.fill(this.loadings, loadingKeys.fetchingItem)

            let self = this
            let params = {
                filter: {
                    active: 1
                },
                pagination: {
                    page: 1,
                    rowsPerPage: 9999
                }
            }
            let response = await calibersService.retrieve(params)
            self.calibers = _.cloneDeep(response.data.results)

            _.pull(this.loadings, loadingKeys.fetchingItem)
        },

        async fetchCategories () {
            _.fill(this.loadings, loadingKeys.fetchingItem)

            let self = this
            let params = {}
            let response = await categoriesService.getAll(params)
            self.categories = _.cloneDeep(response.data.results)

            _.pull(this.loadings, loadingKeys.fetchingItem)
        },

        async fetchProduct () {
            let self = this

            let params = {
                filter: {
                    product_id: self.$route.params.id
                }
            }

            _.fill(this.loadings, loadingKeys.fetchingItem)

            let response = await productsService.retrieve(params)
            self.product = _.cloneDeep(response.data.results[0])

            self.product.calibers_items = await this.fetchProductCalibers()

            self.pictureData = await this.getBase64(self.product.picture_url)
            self.product = _.cloneDeep(self.product)

            _.pull(this.loadings, loadingKeys.fetchingItem)
        },

        async getBase64 (url) {
            return axios
                .get(url, {
                    responseType: 'arraybuffer',
                    Authorization: 'Bearer ' + localStorage.getItem('token')
                })
                .then(response => {
                    let image = btoa(
                        new Uint8Array(response.data).reduce(
                            (data, byte) => data + String.fromCharCode(byte),
                            ''
                        )
                    )

                    return `data:${response.headers['content-type'].toLowerCase()};base64,${image}`
                })
        },

        close () {
            this.$router.go(-1)
        }
    }
}
</script>
